import _ from 'lodash';
import FormattedHTMLMessage from '../../components/FormattedHTMLMessage';
import {FastRobotErrorcode, FastRobotStatusCode, TrayType, ViewMode} from '../../constants/dataEnum';
import {SetupWizardStep} from '../../constants/SetupWizardEnum';
import {changeView, hideLoading, showLoading} from '../actions/appStateAction';
import {clearModalError, hideModal, setModalValid, showModal} from '../actions/modalStateAction';
import {resetWizardState} from '../actions/wizardStateAction';
import {resolveIntl, setErrorCodeNumber} from './commonBusiness';
// eslint-disable-next-line import/no-cycle
import {abortActionBatch, setLEDIndicator} from './instrumentBusiness';
import {getRunningProtocol} from '../../api/protocolManagementApi';
import {parseMessage} from '../../lib/stringHelper';

const intl = resolveIntl();

export function showComingSoonDialog(title) {
  return (dispatch) => {
    const idContentTxt = intl.formatMessage({
      id: 'modal.showComingSoonDialog.text',
    });
    const comingSoonText = (
      <p>
        <FormattedHTMLMessage id={idContentTxt} />
      </p>
    );
    const btn = [
      {
        className: 'btn-lg',
        style: 'primary',
        handlerClick: () => {
          dispatch(hideModal());
        },
        text: 'GOT IT',
      },
    ];
    dispatch(showModal(title, comingSoonText, btn, () => dispatch(hideModal()), 'comming-soon-modal', false));
  };
}

export function showIsBusyModal(userName) {
  const contentId = userName ? 'modal.showIsBusyModal.text.withUser' : 'modal.showIsBusyModal.text';
  return async (dispatch) => {
    await dispatch(setErrorCodeNumber(FastRobotErrorcode.Busy));
    dispatch(
      showModal(
        intl.formatMessage({
          id: 'modal.showIsBusyModal.title',
        }),
        <FormattedHTMLMessage id={contentId} values={{userName}} />,
        [
          {
            className: 'btn-lg',
            style: 'primary',
            handlerClick: () => {
              dispatch(hideModal());
            },
            text: intl.formatMessage({
              id: 'modal.showIsBusyModal.firstButton',
            }),
          },
        ],
        () => {
          dispatch(hideModal());
        },
      ),
    );
  };
}

export function showDefaultErrorModal(errorCode, callback) {
  return (dispatch) => {
    dispatch(
      showModal(
        intl.formatMessage({
          id: 'modal.showDefaultErrorModal.title',
        }),
        <FormattedHTMLMessage id="modal.showDefaultErrorModal.text" values={{errorCode}} />,
        [
          {
            className: 'btn-lg',
            style: 'primary',
            handlerClick: () => {
              dispatch(hideModal());
              dispatch(hideLoading());
              if (!(typeof callback === 'undefined' || callback === null)) {
                callback();
              }
            },
            text: intl.formatMessage({
              id: 'modal.showDefaultErrorModal.firstButton',
            }),
          },
        ],
        () => {
          dispatch(hideModal());
          dispatch(hideLoading());
        },
      ),
    );
  };
}

export function showModalConnectionFailed() {
  return (dispatch) => {
    dispatch(setModalValid());
    dispatch(
      showModal(
        intl.formatMessage({
          id: 'modal.connectionFailedMessage.title',
        }),
        <FormattedHTMLMessage id="modal.connectionFailedMessage.text" />,
        [
          {
            className: 'btn-lg',
            style: 'primary',
            handlerClick: () => {
              // refresh the browser
              window.location.reload();
            },
            text: intl.formatMessage({
              id: 'modal.connectionFailedMessage.firstButton',
            }),
          },
        ],
        () => {
          dispatch(hideModal());
        },
        '',
        true,
      ),
    );
  };
}

export function showModalConnectionLost() {
  return (dispatch) => {
    dispatch(setModalValid());
    dispatch(clearModalError());

    dispatch(
      showModal(
        intl.formatMessage({
          id: 'modal.connectionLostMessage.title',
        }),
        <div>
          <div style={{fontSize: 20, fontWeight: 600}}>
            {intl.formatMessage({
              id: 'modal.connectionLostMessage.text1',
            })}
          </div>
          <br />
          <div>
            <FormattedHTMLMessage id="modal.connectionLostMessage.text2" />
          </div>
        </div>,
        [
          {
            className: 'btn-lg',
            style: 'primary',
            handlerClick: () => {
              dispatch(hideModal());
              window.location.reload();
            },
            text: intl.formatMessage({
              id: 'modal.connectionLostMessage.firstButton',
            }),
          },
        ],
        () => {
          dispatch(hideModal());
        },
        '',
        true,
      ),
    );
  };
}

export const showConfirmationModal =
  (idText, firstButtonCallback = () => {}, secondButtonCallback = () => {}, contentValues = {}) =>
  (dispatch) => {
    dispatch(hideLoading());
    dispatch(
      showModal(
        intl.formatMessage({id: `${idText}.title`}),
        <div className="modal-confirmation">
          <FormattedHTMLMessage id={`${idText}.text`} values={contentValues} />
        </div>,
        [
          {
            style: 'primary',
            handlerClick: () => {
              dispatch(hideModal());
              firstButtonCallback();
            },
            text: intl.formatMessage({id: `${idText}.firstButton`}),
          },
          {
            style: 'secondary',
            handlerClick: () => {
              dispatch(hideModal());
              secondButtonCallback();
            },
            text: intl.formatMessage({id: `${idText}.secondButton`}),
          },
        ],
        () => {},
        '',
        true,
      ),
    );
  };

/**
 * A simplified showModal func. showModal's optional params are converted into object options
 * @param {String} title title of modal
 * @param {String} body body of modal
 * @param {Array} buttons buttons of modal. will automatically hides modal when clicked. consist of object { onClick, label }
 * @param {Object} opt options. options available are { keepErrorCode,  hideClose, modalSize }
 */
export const showModalSimplified =
  (
    title,
    body,
    // eslint-disable-next-line default-param-last
    buttons = [],
    opt,
  ) =>
  (dispatch) => {
    const {keepErrorCode, hideClose, modalSize, className, style, confirmationCheckbox} = opt || {};

    const modalButton = buttons.map((btn) =>
      !btn
        ? null
        : {
            ...btn,
            onClick: () => {
              if (!btn?.keepPreviousModal) dispatch(hideModal());
              if (btn?.onClick) btn?.onClick();
            },
          },
    );

    dispatch(hideLoading());
    dispatch(
      showModal(
        title,
        body,
        _.compact(modalButton),
        () => dispatch(hideModal()),
        className,
        hideClose,
        modalSize,
        style,
        confirmationCheckbox,
        keepErrorCode,
      ),
    );
  };

export const showFormattedModal =
  (
    idText,
    buttonsArray, // type Array. The array content type can be a function or an object with properties of onClick or Label
    opt, // object with the properties of contentValues, hideClose, or modalSize
  ) =>
  (dispatch) => {
    const {contentValues, ...restOfOpt} = opt || {};

    const modalButton = buttonsArray.map((btn) =>
      !btn
        ? null
        : {
            onClick: btn?.onClick || (typeof btn === 'function' ? btn : null),
            label: parseMessage(btn?.label || `${idText}.firstButton`),
            keepPreviousModal: btn?.keepPreviousModal,
          },
    );

    dispatch(hideLoading());
    dispatch(
      showModalSimplified(
        intl.formatMessage({id: `${idText}.title`}),
        <FormattedHTMLMessage id={`${idText}.text`} values={contentValues} />,
        modalButton,
        restOfOpt,
      ),
    );
  };

// #region tray

export const showModalMisalignedTray = (callback) => (dispatch) => {
  dispatch(
    showModal(
      intl.formatMessage({
        id: 'instrumentControlHubBusiness.misalignTray.title',
      }),
      intl.formatMessage({
        id: 'instrumentControlHubBusiness.misalignTray.message',
      }),
      [
        {
          className: 'btn-lg',
          style: 'primary',
          handlerClick: async () => {
            await dispatch(abortActionBatch());
            callback();
          },
          text: intl.formatMessage({
            id: 'instrumentControlHubBusiness.misalignTray.firstButton',
          }),
        },
      ],
      () => {
        dispatch(hideModal());
      },
      'misalign-tray-modal',
      true,
    ),
  );
};

// /**
//  * obsolete.
//  */
// export const showMissingTrayModal = (needRecovery, fastRecovery, afterRecoveryCallback = () => {}) => (dispatch) => {
//   dispatch(addTemporaryState('errorModal', { errorCode: FastRobotErrorcode.MissingTray }));

//   dispatch(
//     showModal(
//       intl.formatMessage({
//         id: 'modal.missingTray.title',
//       }),
//       <FormattedHTMLMessage
//         id="modal.missingTray.text"
//       />,
//       [
//         {
//           className: 'btn-lg',
//           style: 'primary',
//           handlerClick: () => {
//             dispatch(hideModal());
//             if (needRecovery) {
//               const callback = async () => {
//                 dispatch(showLoading());
//                 const result = await dispatch(
//                   invokeAPI(SignalRHub.INSTRUMENT, SignalREvent.SCAN_TRAY_ID_SEQUENCE),
//                 );
//                 dispatch(setTrayID(result));
//                 dispatch(hideLoading());

//                 afterRecoveryCallback();
//               };

//               dispatch(fastRecovery(callback));
//             }
//           },
//           text: needRecovery
//             ? intl.formatMessage({ id: 'modal.missingTray.needRecovery.button' })
//             : intl.formatMessage({
//               id: 'modal.missingTray.firstButton',
//             }),
//         },
//       ],
//     ),
//   );
// };

export const showMissingTrayOnCreateProtocolModal = (callback) => (dispatch) => {
  dispatch(
    showModal(
      intl.formatMessage({
        id: 'modal.missingTrayOnCreateProtocol.title',
      }),
      <FormattedHTMLMessage id="modal.missingTrayOnCreateProtocol.text" />,
      [
        {
          className: 'btn-lg',
          style: 'primary',
          handlerClick: () => {
            dispatch(hideModal());
            if (callback) callback();
          },
          text: intl.formatMessage({id: 'modal.missingTrayOnCreateProtocol.firstButton'}),
        },
      ],
      () => {
        dispatch(hideModal());
      },
      '',
      true,
    ),
  );
};

export const showModalUnsupportedTray =
  (
    // eslint-disable-next-line default-param-last
    useNormalCaddy = false,
    page,
    callback,
  ) =>
  async (dispatch, getState) => {
    const {trayCalibrationState, protocolState} = getState();
    const {trayType} = trayCalibrationState;
    const {trayType: protocolTrayType} = protocolState?.lastOpenedProtocol ?? {};

    const trayName = {
      [TrayType.NORMAL_CADDY]: 'single caddy',
      [TrayType.DOUBLE_CADDY]: 'double caddy',
      [TrayType.HIGH_VOLUME_CADDY]: 'high volume caddy',
    };

    const pageName = {
      [ViewMode.SETUP_WIZARD]: 'setup wizard',
      [ViewMode.PLATE_TYPE_EDITOR]: 'plate definition',
    };

    await dispatch(setErrorCodeNumber(FastRobotErrorcode.UnsupportedTray));

    dispatch(
      showModal(
        intl.formatMessage({
          id: `modal.protocolEditor.unsupportedTray.title${useNormalCaddy ? '.mustUseSingleCaddy' : ''}`,
        }),
        <FormattedHTMLMessage
          id={`modal.protocolEditor.unsupportedTray.text${useNormalCaddy ? '.mustUseSingleCaddy' : ''}`}
          values={{installedTray: trayName[trayType], expectedTray: trayName[protocolTrayType], page: pageName[page]}}
        />,
        [
          {
            className: 'btn-lg',
            style: 'primary',
            handlerClick: () => {
              dispatch(hideModal());
              if (callback) callback();
            },
            text: intl.formatMessage({
              id: 'modal.protocolEditor.unsupportedTray.firstButton',
            }),
          },
        ],
      ),
    );
  };

export const showModalA1OffsetDetectHardwareChanges = (fastRecovery) => async (dispatch) => {
  const settingA1ManuallyConfirmationModal = () => {
    dispatch(
      showFormattedModal('modal.setupWizard.yawTriggeredConfirmation', [
        {
          label: 'modal.setupWizard.yawTriggeredConfirmation.firstButton',
          onClick: () => dispatch(setLEDIndicator(FastRobotStatusCode.IDLE)),
        },
        {
          label: 'modal.setupWizard.yawTriggeredConfirmation.secondButton',
          onClick: () => dispatch(showModalA1OffsetDetectHardwareChanges(fastRecovery)),
          keepPreviousModal: true,
        },
      ]),
    );
  };

  const showModalReinstallHead = () => {
    dispatch(
      showFormattedModal('modal.setupWizard.reinstallHead', [
        {
          label: 'modal.setupWizard.reinstallHead.firstButton',
          onClick: () => dispatch(showModalA1OffsetDetectHardwareChanges(fastRecovery)),
          keepPreviousModal: true,
        },
      ]),
    );
  };

  const abortSetupWizard = async () => {
    dispatch(showLoading());
    dispatch(resetWizardState());
    dispatch(hideModal());
    await dispatch(fastRecovery());
    dispatch(changeView(ViewMode.EXPLORER));
    dispatch(hideLoading());
  };

  dispatch(
    showModal(
      intl.formatMessage({
        id: 'modal.setupWizard.yawTriggered.title',
      }),
      <FormattedHTMLMessage id="modal.setupWizard.yawTriggered.text" />,
      [
        {
          className: 'btn-lg',
          style: 'primary',
          handlerClick: () => {
            showModalReinstallHead();
          },
          text: intl.formatMessage({
            id: 'modal.setupWizard.yawTriggered.firstButton',
          }),
        },
        {
          className: 'btn-lg',
          style: 'secondary',
          handlerClick: () => {
            settingA1ManuallyConfirmationModal();
          },
          text: intl.formatMessage({
            id: 'modal.setupWizard.yawTriggered.secondButton',
          }),
        },
        {
          handlerClick: () => {
            abortSetupWizard();
          },
          text: intl.formatMessage({
            id: 'modal.setupWizard.yawTriggered.thirdButton',
          }),
        },
        '',
        true,
      ],
    ),
  );
};

export const showModalDetectHardwareChange = (fastRecovery) => (dispatch, getState) => {
  const {wizardState, appState} = getState();
  const {stepName} = wizardState;
  const {viewMode} = appState;

  if (viewMode === ViewMode.SETUP_WIZARD) {
    if (stepName === SetupWizardStep.A1_OFFSET_CALIBRATION) {
      dispatch(showModalA1OffsetDetectHardwareChanges(fastRecovery));
    }

    return;
  }

  dispatch(
    showModal(
      intl.formatMessage({
        id: 'modal.detectsHardwareChange.title',
      }),
      <FormattedHTMLMessage id="modal.detectsHardwareChange.text" />,
      [
        {
          className: 'btn-lg',
          style: 'primary',
          handlerClick: () => {
            dispatch(hideModal());
          },
          text: intl.formatMessage({
            id: 'modal.detectsHardwareChange.firstButton',
          }),
        },
      ],
      () => {
        dispatch(hideModal());
      },
      '',
      true,
    ),
  );
};

// #endregion tray

// #region protocol explorer

export const showModalConvertToTessaraProtocol =
  (callback, toTessaraProtocol = true) =>
  (dispatch) => {
    dispatch(
      showModal(
        intl.formatMessage({
          id: `explorer.modal.convert${toTessaraProtocol ? 'ToTessaraProtocol' : 'ToRegularProtocol'}.title`,
        }),
        <FormattedHTMLMessage
          id={`explorer.modal.convert${toTessaraProtocol ? 'ToTessaraProtocol' : 'ToRegularProtocol'}.text`}
        />,
        [
          {
            className: 'btn-lg',
            style: 'primary',
            handlerClick: () => {
              dispatch(hideModal());
              if (callback) callback();
            },
            text: intl.formatMessage({
              id: 'explorer.modal.convertToTessaraProtocol.firstButton',
            }),
          },
          {
            className: 'btn-lg',
            style: 'secondary',
            handlerClick: () => {
              dispatch(hideModal());
            },
            text: intl.formatMessage({
              id: 'explorer.modal.convertToTessaraProtocol.secondButton',
            }),
          },
        ],
        () => {
          dispatch(hideModal());
        },
        '',
        true,
      ),
    );
  };

export function showUpdatePlateDefinitionDialog(title, callback) {
  return (dispatch) => {
    const comingSoonText = (
      <p>
        <FormattedHTMLMessage id="modal.showUpdatePlateDefinitionDialog.text" />
      </p>
    );
    const btn = [
      {
        className: 'btn-lg',
        style: 'primary',
        handlerClick: () => {
          dispatch(hideModal());
          if (callback) callback();
        },
        text: intl.formatMessage({
          id: 'modal.showUpdatePlateDefinitionDialog.reteachPlates',
        }),
      },
      {
        className: 'btn-lg',
        style: 'secondary',
        handlerClick: () => {
          dispatch(hideModal());
        },
        text: intl.formatMessage({
          id: 'modal.showUpdatePlateDefinitionDialog.ignore',
        }),
      },
    ];
    dispatch(showModal(title, comingSoonText, btn, () => dispatch(hideModal()), 'update-plate-modal', true));
  };
}

export function showCloseReteachPlateDialog(title, callback) {
  return (dispatch) => {
    const comingSoonText = (
      <p>
        <FormattedHTMLMessage id="modal.showCloseReteachPlate.text" />
      </p>
    );
    const btn = [
      {
        className: 'btn-lg',
        style: 'primary',
        handlerClick: () => {
          dispatch(hideModal());
          if (callback) callback();
        },
        text: intl.formatMessage({
          id: 'modal.showCloseReteachPlate.gotIt',
        }),
      },
      {
        className: 'btn-lg',
        style: 'secondary',
        handlerClick: () => {
          dispatch(hideModal());
        },
        text: intl.formatMessage({
          id: 'modal.showCloseReteachPlate.cancel',
        }),
      },
    ];
    dispatch(showModal(title, comingSoonText, btn, () => dispatch(hideModal()), 'close-reteach-modal', true));
  };
}

export const showSameFolderNameDetectedModal = (callback) => async (dispatch) => {
  dispatch(
    showModal(
      intl.formatMessage({
        id: 'explorer.modal.sameFolderNameDetected.title',
      }),
      intl.formatMessage({id: 'explorer.modal.sameFolderNameDetected.text'}),
      [
        {
          className: 'btn-lg',
          style: 'primary',
          handlerClick: () => {
            dispatch(hideModal());
            callback(true);
          },
          text: intl.formatMessage({
            id: 'explorer.modal.sameFolderNameDetected.firstButton',
          }),
        },
        {
          className: 'btn-lg',
          style: 'secondary',
          handlerClick: () => {
            dispatch(hideModal());
            callback(false);
          },
          text: intl.formatMessage({
            id: 'explorer.modal.sameFolderNameDetected.secondButton',
          }),
        },
      ],
      () => dispatch(hideModal()),
      '',
      true,
    ),
  );
};
// #endregion protocol explorer

export const checkProtocolExecutorAndShowBusyModal = () => async (dispatch) => {
  const runningProtocolResponse = await getRunningProtocol();
  const {username} = runningProtocolResponse?.payload?.user || {};

  dispatch(showIsBusyModal(username));
};

export const showIdleHumidityConfirmationModal =
  (idText, firstButtonCallback = () => {}, secondButtonCallback = () => {}, content = {}) =>
  (dispatch) => {
    dispatch(hideLoading());
    dispatch(
      showModal(
        intl.formatMessage({id: `${idText}.title`}),
        content,
        [
          {
            style: 'primary',
            handlerClick: () => {
              const isSuccess = firstButtonCallback();
              if (!isSuccess) return;
              dispatch(hideModal());
            },
            text: intl.formatMessage({id: `${idText}.firstButton`}),
          },
          {
            style: 'secondary',
            handlerClick: () => {
              dispatch(hideModal());
              secondButtonCallback();
            },
            text: intl.formatMessage({id: `${idText}.secondButton`}),
          },
        ],
        () => {},
        '',
        true,
      ),
    );
  };

export const showPressureDropModal =
  (
    idText,
    isProtocolRunning,
    // eslint-disable-next-line default-param-last
    firstButtonCallback = () => {},
    // eslint-disable-next-line default-param-last
    secondButtonCallback = () => {},
    thirdButtonCallback,
    contentValues = {},
  ) =>
  (dispatch) => {
    dispatch(hideLoading());

    const buttons = [
      {
        style: 'primary',
        handlerClick: () => {
          dispatch(hideModal());
          firstButtonCallback();
        },
        text: intl.formatMessage({id: `${idText}.firstButton`}),
      },
      {
        // it will only show this button if isProtocolRunning is true
        ...(isProtocolRunning ? {} : {style: 'primary'}),
        type: 'basic',
        handlerClick: () => {
          dispatch(hideModal());
          secondButtonCallback();
        },
        text: intl.formatMessage({id: `${idText}.secondButton`}),
      },
    ];

    if (typeof thirdButtonCallback === 'function') {
      buttons.push({
        type: 'danger',
        handlerClick: () => {
          dispatch(hideModal());
          thirdButtonCallback();
        },
        text: intl.formatMessage({id: `${idText}.thirdButton`}),
      });
    }

    dispatch(
      showModal(
        intl.formatMessage({id: `${idText}.title`}),
        <div className="modal-confirmation">
          <FormattedHTMLMessage id={`${idText}.text`} values={contentValues} />
        </div>,
        buttons,
        () => {},
        '',
        true,
      ),
    );
  };

export function showUntrainedPlatesModal(untrainedNestIds, startRunProtocol) {
  return (dispatch) => {
    const title = intl.formatMessage({
      id: 'modal.UntrainedPlate.title',
    });

    const formattedNestList = intl.formatList(
      untrainedNestIds.map((id) => `nest ${id}`),
      {type: 'conjunction'},
    );

    const modalText = <FormattedHTMLMessage id="modal.UntrainedPlate.text" values={{nests: formattedNestList}} />;

    const handlerClick = () => {
      startRunProtocol();
      dispatch(hideModal());
    };

    const btn = [
      {
        className: 'btn-lg',
        style: 'primary',
        handlerClick,
        text: 'GOT IT',
      },
    ];
    dispatch(showModal(title, modalText, btn, handlerClick, 'untrained-plates-modal', true));
  };
}

export const showLowWaterLevelModal =
  (firstButtonCallback = () => {}, secondButtonCallback = () => {}, contentValues = {}) =>
  async (dispatch) => {
    dispatch(hideLoading());
    await dispatch(setErrorCodeNumber(FastRobotErrorcode.FAILED_TO_DETECT_WATER));

    const idText = 'modal.humidifier.lowWaterLevel.warning';

    const buttons = [
      {
        style: 'primary',
        handlerClick: () => {
          dispatch(hideModal());
          firstButtonCallback();
        },
        text: intl.formatMessage({id: `${idText}.firstButton`}),
      },
      {
        type: 'danger',
        handlerClick: () => {
          dispatch(hideModal());
          secondButtonCallback();
        },
        text: intl.formatMessage({id: `${idText}.secondButton`}),
      },
    ];

    dispatch(
      showModal(
        intl.formatMessage({id: `${idText}.title`}),
        <div className="modal-confirmation">
          <FormattedHTMLMessage id={`${idText}.text`} values={contentValues} />
        </div>,
        buttons,
        () => {},
        '',
        true,
      ),
    );
  };
